import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/authStore";
import NotificationBell from "@/components/common/NotificationBell";
import { AgentProvider } from "@/components/agent/AgentProvider";
import MakejWidget from "@/components/agent/MakejWidget";
import {
  LayoutDashboard,
  MapPin,
  Users,
  Plane,
  ShoppingCart,
  UserCircle,
  Inbox,
  Sparkles,
  Zap,
  Workflow,
  Image,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  ScrollText,
  BookOpen,
  ChevronDown,
  Shield,
  FileText,
  BarChart3,
  Gift,
  ClipboardList,
  Activity,
  Newspaper,
  HelpCircle,
  Home,
  Cloud,
  UserPlus,
  Building2,
  Package,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  LayoutGrid,
  Receipt,
  Wallet,
  FileSignature,
  MessageSquareWarning,
  Globe2,
  type LucideIcon,
} from "lucide-react";
import clsx from "clsx";

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  badge?: string;
}

interface NavSection {
  /** i18n key under `nav.*`. */
  titleKey: string;
  items: NavItem[];
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

// Grouped sidebar (vision §3). New items (Inquiries, Inbox) are added to their
// section as their pages land in later Block 1 slices.
const navSections: NavSection[] = [
  {
    titleKey: "nav.myDay",
    items: [{ name: "Dashboard", href: "/", icon: LayoutDashboard }],
  },
  {
    titleKey: "nav.inbox",
    items: [{ name: "Inbox", href: "/inbox", icon: Inbox }],
  },
  {
    titleKey: "nav.sales",
    items: [
      { name: "Inquiries", href: "/leads", icon: UserPlus },
      { name: "Orders", href: "/orders", icon: ShoppingCart },
      { name: "Customers", href: "/customers", icon: UserCircle },
    ],
  },
  {
    titleKey: "nav.operations",
    items: [
      { name: "Service queue", href: "/services", icon: ClipboardList },
      { name: "Flight desk", href: "/flights", icon: Plane },
      { name: "Season", href: "/season", icon: LayoutGrid },
      { name: "Schedule", href: "/schedule", icon: CalendarClock },
      { name: "Departures", href: "/departures", icon: CalendarRange },
      { name: "Calendar", href: "/calendar", icon: CalendarDays },
      { name: "Documents", href: "/documents", icon: FileSignature },
      { name: "Complaints", href: "/complaints", icon: MessageSquareWarning },
      { name: "Suppliers", href: "/suppliers", icon: Building2 },
      { name: "Service catalog", href: "/supplier-components", icon: Package },
      { name: "Templates", href: "/templates", icon: FileText },
    ],
  },
  {
    titleKey: "nav.finance",
    items: [
      { name: "Invoices", href: "/invoices", icon: Receipt },
      { name: "Payments & margin", href: "/finance", icon: Wallet },
      { name: "Vouchers", href: "/vouchers", icon: Gift },
    ],
  },
  {
    titleKey: "nav.content",
    collapsible: true,
    defaultCollapsed: true,
    items: [
      { name: "Destinations", href: "/destinations", icon: MapPin },
      { name: "Guides", href: "/guides", icon: Users },
      { name: "Dest. Teams", href: "/destination-teams", icon: Users },
      { name: "Trips", href: "/trips", icon: Plane },
      { name: "Upgrades", href: "/premium-upgrades", icon: Sparkles },
      { name: "Inspirations", href: "/inspirations", icon: Activity },
      { name: "FAQ", href: "/faqs", icon: HelpCircle },
      { name: "Blog", href: "/blog", icon: Newspaper },
      { name: "Reviews", href: "/reviews", icon: MessageSquare },
      { name: "Quiz", href: "/quiz", icon: ClipboardList },
      { name: "Homepage", href: "/homepage", icon: Home },
      { name: "Media", href: "/media", icon: Image },
    ],
  },
  {
    titleKey: "nav.marketing",
    items: [
      { name: "NL Studio", href: "/newsletter-studio", icon: Sparkles },
      { name: "Automations", href: "/automations", icon: Zap },
      { name: "Journeys", href: "/journeys", icon: Workflow },
    ],
  },
  {
    titleKey: "nav.system",
    items: [
      { name: "Analytics", href: "/analytics", icon: BarChart3 },
      { name: "Wiki", href: "/wiki", icon: BookOpen },
      { name: "Country KB", href: "/country-kb", icon: Globe2 },
      { name: "Audit log", href: "/audit", icon: ScrollText },
      { name: "Users", href: "/users", icon: Shield },
      { name: "Integrations", href: "/integrations", icon: Cloud },
    ],
  },
];

const flatNav = navSections.flatMap((s) => s.items);

function isItemActive(href: string, pathname: string): boolean {
  return href === "/" ? pathname === "/" : pathname.startsWith(href);
}

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();

  // Per-section collapse state, seeded from defaults. A section is force-expanded
  // when the active route lives inside it (so you never lose your place).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      navSections.filter((s) => s.collapsible).map((s) => [s.titleKey, !!s.defaultCollapsed]),
    ),
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const currentTitle =
    flatNav.find((n) => isItemActive(n.href, location.pathname))?.name || t("common.admin");

  return (
    <AgentProvider>
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={clsx(
            "bg-sidebar fixed inset-y-0 left-0 z-50 flex w-64 transform flex-col transition-transform duration-200 ease-in-out lg:static lg:translate-x-0",
            sidebarOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-6">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-white.svg" alt="Banh Mi Travel" className="h-8 w-auto" />
              <span className="bg-accent-500 rounded px-1.5 py-0.5 text-xs font-medium text-white">
                {t("common.admin")}
              </span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-white lg:hidden"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="scrollbar-hide mt-4 flex-1 space-y-4 overflow-y-auto px-3 pb-4">
            {navSections.map((section) => {
              const hasActive = section.items.some((i) => isItemActive(i.href, location.pathname));
              const isCollapsed = section.collapsible && collapsed[section.titleKey] && !hasActive;
              return (
                <div key={section.titleKey}>
                  <button
                    type="button"
                    onClick={() =>
                      section.collapsible &&
                      setCollapsed((prev) => ({
                        ...prev,
                        [section.titleKey]: !prev[section.titleKey],
                      }))
                    }
                    className={clsx(
                      "flex w-full items-center justify-between px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400",
                      section.collapsible && "hover:text-gray-200",
                    )}
                  >
                    <span>{t(section.titleKey)}</span>
                    {section.collapsible && (
                      <ChevronDown
                        className={clsx(
                          "h-3.5 w-3.5 transition-transform",
                          isCollapsed && "-rotate-90",
                        )}
                      />
                    )}
                  </button>

                  {!isCollapsed &&
                    section.items.map((item) => {
                      const isActive = isItemActive(item.href, location.pathname);
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={clsx(
                            "mb-0.5 flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors",
                            isActive
                              ? "bg-primary-600 text-white"
                              : "text-gray-300 hover:bg-white/10 hover:text-white",
                          )}
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                          <span className="truncate">{item.name}</span>
                          {item.badge && (
                            <span className="bg-accent-500 ml-auto rounded px-1.5 py-0.5 text-[10px] font-semibold text-white">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                </div>
              );
            })}
          </nav>

          {/* Bottom section */}
          <div className="mt-auto border-t border-gray-700 p-4">
            <Link
              to="/settings"
              className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Settings className="h-5 w-5" />
              {t("common.settings")}
            </Link>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Top header */}
          <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 lg:px-6 dark:border-gray-700 dark:bg-gray-800">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-600 hover:text-gray-900 lg:hidden dark:text-gray-300 dark:hover:text-white"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Breadcrumb / Title */}
            <div className="hidden lg:block">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {currentTitle}
              </h1>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <NotificationBell />

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <div className="bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 flex h-8 w-8 items-center justify-center rounded-full">
                    {user?.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:block">{user?.name}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 z-50 mt-2 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                      <div className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user?.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                        <p className="text-primary-600 dark:text-primary-400 mt-1 text-xs">
                          {user?.role}
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <LogOut className="h-4 w-4" />
                        {t("common.logout")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-auto p-4 lg:p-6">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Floating "MAKEJ!" AI assistant — global, route-aware, hidden until key configured */}
      <MakejWidget />
    </AgentProvider>
  );
}
